import { Link } from "@tanstack/react-router";
import { Instagram, Linkedin, Star, MessageCircle, Mail, Phone, MapPin } from "lucide-react";
import { site } from "@/lib/site";
import { SiteLink } from "@/components/site/SiteLink";
import logo from "@/assets/logo.webp";

const exploreLinks = [
  { to: "/services", label: "Services" },
  { to: "/pricing", label: "Pricing" },
  
  { to: "/achievements", label: "Wins" },
  { to: "/about", label: "About us" },
  { to: "/contact", label: "Contact" },
] as const;

const guideLinks = [
  { to: "/resources", label: "All F1 Visa Guides" },
  { to: "/refusal-decoder", label: "214(b) Refusal Decoder" },
  { to: "/resources/214b-vs-221g-difference", label: "214(b) vs 221(g)" },
  { to: "/f1-visa-interview-questions", label: "F1 Interview Questions" },
  { to: "/us-visa-scheduling-guide", label: "Visa Scheduling Guide" },
  { to: "/refusal-case-study", label: "Refusal Case Study" },
  { to: "/refusal-victories", label: "214(b) Refusal Reversed" },
  { to: "/find-your-plan", label: "Find Your Plan" },
] as const;

const legalLinks = [
  { to: "/terms", label: "Terms & Conditions" },
  { to: "/privacy", label: "Privacy Policy" },
  { to: "/refund", label: "Refund Policy" },
  { to: "/shipping-delivery", label: "Shipping & Delivery" },
] as const;

export function Footer() {
  return (
    <footer className="relative isolate overflow-hidden bg-ink text-ink-foreground mt-12 pb-16 md:pb-0">
      {/* Ambient glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 -top-32 h-60 opacity-50 blur-3xl"
        style={{
          background:
            "radial-gradient(60% 60% at 50% 0%, color-mix(in oklab, var(--color-primary) 35%, transparent), transparent 70%)",
        }}
      />

      <div className="relative mx-auto max-w-6xl px-5 py-8 space-y-6 text-xs">
        {/* Top Grid: Brand & Link Columns */}
        <div className="grid gap-6 sm:grid-cols-2 md:grid-cols-12 items-start">
          {/* Brand & Socials */}
          <div className="md:col-span-3 space-y-3">
            <div className="flex items-center gap-2">
              <img
                src={logo}
                alt="WeLivdIt Official Logo"
                width={36}
                height={36}
                loading="lazy"
                decoding="async"
                className="h-9 w-9 rounded-full bg-white object-contain p-0.5"
              />
              <span className="font-display text-lg font-semibold">
                We<span className="text-primary">Livd</span>It
              </span>
            </div>
            <p className="text-ink-foreground/70 leading-relaxed max-w-xs text-xs">
              Real mocks & DS-160 review to your final visa interview walk-in.
            </p>

            <div className="flex items-center gap-2">
              <div className="flex">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} className="h-3.5 w-3.5 fill-gold stroke-gold" />
                ))}
              </div>
              <span className="text-ink-foreground/80 text-[11px]">
                {site.rating.score} · {site.rating.count} reviews
              </span>
            </div>

            <div className="flex items-center gap-2 pt-1">
              <a
                href={site.socials.instagram}
                target="_blank"
                rel="noreferrer"
                aria-label="Instagram"
                className="h-7 w-7 grid place-items-center rounded-full bg-white/10 hover:bg-primary hover:text-primary-foreground transition"
              >
                <Instagram className="h-3.5 w-3.5" />
              </a>
              <a
                href={site.socials.linkedin}
                target="_blank"
                rel="noreferrer"
                aria-label="LinkedIn"
                className="h-7 w-7 grid place-items-center rounded-full bg-white/10 hover:bg-primary hover:text-primary-foreground transition"
              >
                <Linkedin className="h-3.5 w-3.5" />
              </a>
              <a
                href={site.whatsapp}
                target="_blank"
                rel="nofollow noopener noreferrer"
                aria-label="WhatsApp"
                className="h-7 w-7 grid place-items-center rounded-full bg-white/10 hover:bg-primary hover:text-primary-foreground transition"
              >
                <MessageCircle className="h-3.5 w-3.5" />
              </a>
            </div>
          </div>

          {/* Quick Nav Columns */}
          <div className="md:col-span-2">
            <h3 className="text-[10px] font-semibold tracking-[0.16em] uppercase text-ink-foreground/60 mb-2.5">
              Explore
            </h3>
            <ul className="space-y-1.5">
              {exploreLinks.map((l) => (
                <li key={l.to}>
                  <SiteLink path={l.to} className="text-ink-foreground/80 hover:text-primary transition">
                    {l.label}
                  </SiteLink>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-3">
            <h3 className="text-[10px] font-semibold tracking-[0.16em] uppercase text-ink-foreground/60 mb-2.5">
              F1 Visa Guides &amp; Resources
            </h3>
            <ul className="space-y-1.5">
              {guideLinks.map((l) => (
                <li key={l.to}>
                  <SiteLink path={l.to} className="text-ink-foreground/80 hover:text-primary transition">
                    {l.label}
                  </SiteLink>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-2">

            <h3 className="text-[10px] font-semibold tracking-[0.16em] uppercase text-ink-foreground/60 mb-2.5">
              Legal
            </h3>
            <ul className="space-y-1.5">
              {legalLinks.map((l) => (
                <li key={l.to}>
                  <SiteLink path={l.to} className="text-ink-foreground/80 hover:text-primary transition">
                    {l.label}
                  </SiteLink>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact & Grievance */}
          <div className="md:col-span-2 space-y-2">
            <h3 className="text-[10px] font-semibold tracking-[0.16em] uppercase text-ink-foreground/60 mb-2.5">
              Contact
            </h3>
            <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-ink-foreground/80">
              <a
                href={`tel:${site.phoneTel}`}
                className="inline-flex items-center gap-1.5 hover:text-primary transition"
              >
                <Phone className="h-3.5 w-3.5 text-primary" /> {site.phone}
              </a>
              <a
                href={`mailto:${site.email}`}
                className="inline-flex items-center gap-1.5 hover:text-primary transition"
              >
                <Mail className="h-3.5 w-3.5 text-primary" /> {site.email}
              </a>
            </div>
            <p className="flex items-start gap-1.5 text-ink-foreground/70 leading-relaxed text-[11px]">
              <MapPin className="h-3.5 w-3.5 mt-0.5 shrink-0 text-primary" />
              <span>Plot No-57, Phase-2, JPN Nagar, Miyapur, Hyderabad - 500049</span>
            </p>
          </div>
        </div>

        {/* Bottom Strip: Legal & Copyright combined into 1 clean bar */}
        <div className="border-t border-white/10 pt-4 flex flex-col sm:flex-row items-center justify-between gap-2 text-[11px] text-ink-foreground/60">
          <span>
            © 2026 {site.legalName} (CIN: {site.cin}). All rights reserved.
          </span>
          <div className="flex items-center gap-4">
            <span>Grievance: Srinath (Founder)</span>
            <a
              href={site.socials.trustpilot}
              target="_blank"
              rel="noreferrer"
              className="hover:text-primary transition"
            >
              Trustpilot →
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
